import { useId, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import type { OverviewFilters, OverviewKpiSeries } from "@nexus/contracts";
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
} from "@nexus/ui";
import { useTranslation } from "react-i18next";
import type { MyBrandFiltersInput } from "@/lib/queries/dashboard.queries";
import {
  buildTopicDetailFatigueSearch,
  overviewKeys,
  topicDetailFatigueQueryOptions,
} from "@/lib/queries/overview.queries";
import { useTopicDetailAsyncResource } from "../../hooks/useTopicDetailAsyncResource.js";
import { formatMetricValue } from "@/features/dashboard/lib/format";
import { useTenantCurrency } from "@/lib/use-tenant-currency";
import type { TopicDetailContext } from "../../lib/topicDetailContext.js";
import { fatigueKpiDisplayFormat } from "../../lib/fatigueKpiFormat.js";
import { visibleTopicDetailKpis } from "../../lib/topicDetailKpis.js";
import { useKpiCatalog } from "../../hooks/useKpiCatalog.js";
import { useKpiSelectorCatalog } from "@/features/kpi-settings/hooks/useKpiSelectorCatalog";
import { usePrefersReducedMotion } from "../OverviewChart/usePrefersReducedMotion.js";
import {
  buildFatigueChartRows,
  fatigueSeriesColor,
  fatigueSeriesDisplayName,
} from "../../lib/buildFatigueChartRows.js";

interface TopicDetailFatigueTabProps {
  tenantId: string;
  context: TopicDetailContext;
  overviewFilters: OverviewFilters;
  globalFilters: MyBrandFiltersInput;
  kpiSeries: OverviewKpiSeries[];
}

export function TopicDetailFatigueTab({
  tenantId,
  context,
  overviewFilters,
  globalFilters,
  kpiSeries,
}: TopicDetailFatigueTabProps) {
  const { t } = useTranslation();
  const { symbol: currencySymbol } = useTenantCurrency();
  const { visible, metricCatalog } = useKpiCatalog("overview");
  const { metricById } = useKpiSelectorCatalog("overview");
  const kpiOptions = useMemo<string[]>(() => {
    const available = new Set(kpiSeries.map((s) => s.kpi));
    const fromCatalog = visibleTopicDetailKpis(visible).map((row) => row.name);
    return fromCatalog.filter((kpi) => available.has(kpi));
  }, [kpiSeries, visible]);
  const [selectedKpi, setSelectedKpi] = useState(kpiOptions[0] ?? "Spend");

  const querySearch = buildTopicDetailFatigueSearch(
    overviewFilters,
    globalFilters,
    context,
    selectedKpi,
  );

  const { data: summary } = useQuery(
    topicDetailFatigueQueryOptions(
      tenantId,
      overviewFilters,
      globalFilters,
      context,
      selectedKpi,
    ),
  );

  const resetKey = `${tenantId}:${querySearch}`;
  const { data, isLoading, isError } = useTopicDetailAsyncResource({
    summary: summary ?? undefined,
    targetKey: overviewKeys.topicDetailFatigue(
      tenantId,
      overviewFilters,
      globalFilters,
      context,
      selectedKpi,
    ),
    resetKey,
    enabled: !!tenantId && !!context && !!selectedKpi,
  });

  const valueFormat = useMemo(
    () => fatigueKpiDisplayFormat(selectedKpi, metricCatalog),
    [selectedKpi, metricCatalog],
  );
  const selectedDecimals = metricCatalog.find((e) => e.id === selectedKpi)?.decimals ?? 2;

  const formatValue = useMemo(
    () => (value: number) => formatMetricValue(value, valueFormat, currencySymbol, selectedDecimals),
    [valueFormat, currencySymbol, selectedDecimals],
  );

  const chartModel = useMemo(() => buildFatigueChartRows(data ?? []), [data]);
  const kpiSeriesColor = metricById[selectedKpi]?.colorVar ?? "var(--primary)";
  const chartInstanceId = useId().replace(/:/g, "");
  const prefersReducedMotion = usePrefersReducedMotion();

  if (kpiOptions.length === 0) {
    return (
      <div className="flex flex-col gap-4 px-6 py-5">
        <p className="text-sm text-muted-foreground">
          {t("overview.topicDetail.noFatigueKpis", { defaultValue: "No KPIs available for fatigue chart." })}
        </p>
      </div>
    );
  }

  const chartContent = (() => {
    if (isLoading) {
      return <Skeleton className="h-[320px] w-full rounded-lg" />;
    }
    if (isError) {
      return (
        <p className="text-sm text-destructive">
          {t("overview.topicDetail.fatigueError", { defaultValue: "Failed to load fatigue data." })}
        </p>
      );
    }
    if (chartModel.rows.length === 0) {
      return (
        <p className="text-sm text-muted-foreground">
          {t("overview.topicDetail.noFatigueData", { defaultValue: "No fatigue data for this topic." })}
        </p>
      );
    }

    const split = chartModel.isSplit;
    const gradientIdFor = (dataKey: string) =>
      `fatigue-${chartInstanceId}-${dataKey.replace(/[^a-zA-Z0-9_-]/g, "_")}`;

    return (
      <div className="flex w-full flex-col gap-3">
        <div className="h-[320px] w-full">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={chartModel.rows} margin={{ top: 8, right: 16, bottom: 8, left: 8 }}>
              <defs>
                {split
                  ? chartModel.series.map((series, index) => {
                      const color = fatigueSeriesColor(index);
                      const gradientId = gradientIdFor(series.dataKey);
                      return (
                        <linearGradient key={gradientId} id={gradientId} x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor={color} stopOpacity={0.3} />
                          <stop offset="100%" stopColor={color} stopOpacity={0} />
                        </linearGradient>
                      );
                    })
                  : (
                    <linearGradient id={`fatigue-${chartInstanceId}-value`} x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={kpiSeriesColor} stopOpacity={0.3} />
                      <stop offset="100%" stopColor={kpiSeriesColor} stopOpacity={0} />
                    </linearGradient>
                  )}
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
              <XAxis
                dataKey="date"
                tick={{ fontSize: 11, fill: "var(--muted-foreground)" }}
                tickLine={false}
                axisLine={false}
              />
              <YAxis
                tick={{ fontSize: 11, fill: "var(--muted-foreground)" }}
                tickLine={false}
                axisLine={false}
                width={56}
                tickFormatter={formatValue}
              />
              <Tooltip
                contentStyle={{
                  background: "var(--card)",
                  border: "1px solid var(--border)",
                  borderRadius: 8,
                  fontSize: 12,
                }}
                formatter={(value) => formatValue(typeof value === "number" ? value : 0)}
              />
              {split
                ? chartModel.series.map((series, index) => {
                    const color = fatigueSeriesColor(index);
                    return (
                      <Area
                        key={series.dataKey}
                        type="monotone"
                        dataKey={series.dataKey}
                        name={fatigueSeriesDisplayName(series.raw)}
                        stroke={color}
                        strokeWidth={2}
                        fill={`url(#${gradientIdFor(series.dataKey)})`}
                        dot={false}
                        activeDot={{ r: 4 }}
                        isAnimationActive={!prefersReducedMotion}
                        animationDuration={900}
                        animationEasing="ease-out"
                      />
                    );
                  })
                : (
                  <Area
                    type="monotone"
                    dataKey="value"
                    name={selectedKpi}
                    stroke={kpiSeriesColor}
                    strokeWidth={2}
                    fill={`url(#fatigue-${chartInstanceId}-value)`}
                    dot={false}
                    activeDot={{ r: 4 }}
                    isAnimationActive={!prefersReducedMotion}
                    animationDuration={900}
                    animationEasing="ease-out"
                  />
                )}
            </AreaChart>
          </ResponsiveContainer>
        </div>
        {split ? (
          <ul
            className="flex flex-wrap justify-center gap-x-4 gap-y-1.5"
            aria-label={t("overview.topicDetail.fatigueSeriesLegend", { defaultValue: "Chart series" })}
          >
            {chartModel.series.map((series, index) => (
              <li key={series.dataKey} className="flex items-center gap-1.5">
                <span
                  className="h-3 w-3 shrink-0 rounded-[3px]"
                  style={{ background: fatigueSeriesColor(index) }}
                  aria-hidden
                />
                <span className="text-[11px] text-muted-foreground">
                  {fatigueSeriesDisplayName(series.raw)}
                </span>
              </li>
            ))}
          </ul>
        ) : null}
      </div>
    );
  })();

  return (
    <div className="flex flex-col gap-4 px-6 py-5">
      <div className="flex items-center justify-between gap-3">
        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          {t("overview.topicDetail.fatigueTitle", { defaultValue: "Fatigue over time" })}
        </p>
        <Select value={selectedKpi} onValueChange={setSelectedKpi}>
          <SelectTrigger className="h-8 w-[160px] text-xs">
            <SelectValue placeholder={t("overview.topicDetail.selectKpi", { defaultValue: "Select KPI" })} />
          </SelectTrigger>
          <SelectContent>
            {kpiOptions.map((kpi) => (
              <SelectItem key={kpi} value={kpi}>
                {kpi}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {chartContent}
    </div>
  );
}
